import { useCallback } from "react";
import { toast } from "sonner";
import { sharedApi } from "@/lib/api";
import { readFileAsDataUrl } from "@/lib/fileReader";

const MAX_IMAGE_BYTES = 6 * 1024 * 1024; // spec: ~6 MB per image
const MAX_VIDEO_BYTES = 2 * 1024 * 1024;
// Iter 736 — per-PDF limit raised 2 MB → 10 MB (matches backend).
const MAX_DOC_BYTES = 10 * 1024 * 1024;
const MAX_LOGO_BYTES = 2 * 1024 * 1024;
const MAX_DOCS = 3;

/**
 * Centralised media upload handlers for the post-ad flow. Owning them in a
 * hook lets each section stay declarative while keeping validation/toast
 * copy consistent across image, video, PDF, agency-logo and secondary-
 * contact-photo uploads.
 */
export default function useAdUploads({ form, set, maxImages }) {
  const uploadImage = useCallback(
    async (file) => {
      if (form.images.length >= maxImages) {
        toast.error(`Max ${maxImages} photos for this category.`);
        return;
      }
      if (file.size > MAX_IMAGE_BYTES) {
        toast.error("Each photo must be under 6 MB.");
        return;
      }
      const dataUri = await readFileAsDataUrl(file);
      try {
        const { data } = await sharedApi.post("/uploads/image", { data: dataUri });
        if (data.url) set({ images: [...form.images, data.url] });
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Image upload failed");
      }
    },
    [form.images, maxImages, set]
  );

  const addImageUrl = useCallback(
    async (rawUrl) => {
      const url = (rawUrl || "").trim();
      if (!url) return;
      if (form.images.length >= maxImages) {
        toast.error(`Max ${maxImages} photos for this category.`);
        return;
      }
      try {
        const { data } = await sharedApi.post("/uploads/image", { data: url });
        set({ images: [...form.images, data.url || url] });
      } catch (err) {
        // Iter-624 — never hotlink the raw remote URL as a fallback:
        // un-owned URLs can expire or swap content later, which is how
        // listings end up carrying a wrong photo. Fail loudly instead.
        toast.error(
          err?.response?.data?.detail ||
            "We couldn't fetch that image URL — save the photo and upload it instead.",
        );
      }
    },
    [form.images, maxImages, set]
  );

  const uploadAgencyLogo = useCallback(
    async (file) => {
      if (file.size > MAX_LOGO_BYTES) {
        toast.error("Logo must be under 2 MB.");
        return;
      }
      if (/\.svg$/i.test(file.name) || file.type === "image/svg+xml") {
        toast.error("SVG logos aren't supported — please upload PNG or JPG.");
        return;
      }
      const dataUri = await readFileAsDataUrl(file);
      try {
        const { data } = await sharedApi.post("/uploads/image", { data: dataUri });
        if (data.url) set({ agency_logo: data.url });
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Logo upload failed");
      }
    },
    [set]
  );

  const uploadSecondaryContactPhoto = useCallback(
    async (file) => {
      if (file.size > MAX_LOGO_BYTES) {
        toast.error("Photo must be under 2 MB.");
        return;
      }
      const dataUri = await readFileAsDataUrl(file);
      try {
        const { data } = await sharedApi.post("/uploads/image", { data: dataUri });
        if (data.url) set({ secondary_contact_photo: data.url });
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Photo upload failed");
      }
    },
    [set]
  );

  const uploadVideo = useCallback(
    async (file) => {
      if (file.size > MAX_VIDEO_BYTES) {
        toast.error("Video must be under 2 MB.");
        return;
      }
      const dataUri = await readFileAsDataUrl(file);
      set({ video: dataUri });
      toast.success("Video attached.");
    },
    [set]
  );

  const uploadDoc = useCallback(
    async (file) => {
      if (form.documents.length >= MAX_DOCS) {
        toast.error(`Max ${MAX_DOCS} PDFs.`);
        return;
      }
      if (file.size > MAX_DOC_BYTES) {
        toast.error("PDF must be under 10 MB.");
        return;
      }
      if (file.type !== "application/pdf") {
        toast.error("Only PDF files.");
        return;
      }
      const dataUri = await readFileAsDataUrl(file);
      set({
        documents: [...form.documents, { name: file.name.slice(0, 80), data: dataUri }],
      });
    },
    [form.documents, set]
  );

  return {
    uploadImage,
    addImageUrl,
    uploadAgencyLogo,
    uploadSecondaryContactPhoto,
    uploadVideo,
    uploadDoc,
    MAX_DOCS,
  };
}
